import type { User } from '@supabase/supabase-js';
import { normalizeVisibleEmail } from '@/lib/portalAuthIdentity';
import { getPortalOrigin } from '@/lib/selfHostedUrls';

type GoogleAuthPortal = 'hire' | 'seek';
type GoogleAuthUserType = 'employer' | 'candidate';

export const LOCAL_GOOGLE_AUTH_ORIGIN = 'http://127.0.0.1:8080';
export const LOCALHOST_GOOGLE_AUTH_ORIGIN = 'http://localhost:8080';

const GOOGLE_AUTH_CALLBACK_PATHS: Record<GoogleAuthPortal, string> = {
  hire: '/hire/auth/callback',
  seek: '/seek/auth/callback',
};

const APPROVED_LOCAL_GOOGLE_AUTH_ORIGINS = new Set([
  LOCAL_GOOGLE_AUTH_ORIGIN,
  LOCALHOST_GOOGLE_AUTH_ORIGIN,
]);

type GoogleAuthConfigurationErrorCode =
  | 'google_oauth_portal_required'
  | 'google_oauth_public_origin_missing'
  | 'google_oauth_public_origin_invalid';

export class GoogleAuthConfigurationError extends Error {
  constructor(
    readonly code: GoogleAuthConfigurationErrorCode,
    readonly portal?: GoogleAuthPortal,
  ) {
    super(code);
    this.name = 'GoogleAuthConfigurationError';
  }
}

export function getGoogleOAuthQueryParams() {
  return {
    // Fork logout clears the Supabase session, not the browser's Google SSO
    // session. Always show Google's chooser so a returning user is not
    // silently signed straight back into the previous Fork account.
    prompt: 'select_account',
  } as const;
}

export function isLocalGoogleAuthFallbackHost(hostname: string) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function assertGoogleAuthPortal(portal: unknown): asserts portal is GoogleAuthPortal {
  if (portal !== 'hire' && portal !== 'seek') {
    throw new GoogleAuthConfigurationError('google_oauth_portal_required');
  }
}

function getConfiguredGoogleAuthOrigin(portal: GoogleAuthPortal) {
  const envKey = portal === 'seek' ? 'VITE_PUBLIC_SEEK_URL' : 'VITE_PUBLIC_HIRE_URL';
  const configuredValue = import.meta.env[envKey]?.trim();

  if (!configuredValue) {
    throw new GoogleAuthConfigurationError('google_oauth_public_origin_missing', portal);
  }

  let configuredUrl: URL;
  try {
    configuredUrl = new URL(getPortalOrigin(portal));
  } catch {
    throw new GoogleAuthConfigurationError('google_oauth_public_origin_invalid', portal);
  }

  const isOriginOnly =
    !configuredUrl.username &&
    !configuredUrl.password &&
    configuredUrl.pathname === '/' &&
    !configuredUrl.search &&
    !configuredUrl.hash;
  const isApprovedLocalOrigin = APPROVED_LOCAL_GOOGLE_AUTH_ORIGINS.has(configuredUrl.origin);
  const usesApprovedProtocol = configuredUrl.protocol === 'https:' || isApprovedLocalOrigin;

  if (!isOriginOnly || !usesApprovedProtocol) {
    throw new GoogleAuthConfigurationError('google_oauth_public_origin_invalid', portal);
  }

  return configuredUrl.origin;
}

export function isGoogleAuthEnabled(portal: GoogleAuthPortal) {
  const envKey = portal === 'seek' ? 'VITE_ENABLE_SEEK_GOOGLE_AUTH' : 'VITE_ENABLE_HIRE_GOOGLE_AUTH';
  const configuredValue = import.meta.env[envKey];

  if (configuredValue === 'true') return true;
  if (configuredValue === 'false') return false;

  return typeof window !== 'undefined' && isLocalGoogleAuthFallbackHost(window.location.hostname);
}

export function getGoogleAuthCallbackUrl(portal: GoogleAuthPortal, currentUrl?: string | URL) {
  assertGoogleAuthPortal(portal);
  const browserUrl = new URL(currentUrl?.toString() ?? window.location.href);
  const origin = APPROVED_LOCAL_GOOGLE_AUTH_ORIGINS.has(browserUrl.origin)
    ? browserUrl.origin
    : getConfiguredGoogleAuthOrigin(portal);

  return new URL(GOOGLE_AUTH_CALLBACK_PATHS[portal], `${origin}/`).toString();
}

export function getGoogleAuthConfigurationErrorCode(error: unknown) {
  return error instanceof GoogleAuthConfigurationError
    ? error.code
    : 'google_oauth_public_origin_invalid';
}

function isInternalAuthEmail(value?: string | null) {
  return value?.toLowerCase().includes('auth.fork.internal') ?? false;
}

function getStringMetadata(user: User, key: string) {
  const value = user.user_metadata?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeMetadataValue(value: string) {
  return value.trim().toLowerCase();
}

export function getSafeGoogleVisibleEmail(user: User) {
  const metadataVisibleEmail = getStringMetadata(user, 'visible_email');
  if (metadataVisibleEmail && !isInternalAuthEmail(metadataVisibleEmail)) {
    return normalizeVisibleEmail(metadataVisibleEmail);
  }

  if (user.email && !isInternalAuthEmail(user.email)) {
    return normalizeVisibleEmail(user.email);
  }

  return '';
}

export function getGoogleFullName(user: User) {
  return getStringMetadata(user, 'full_name') || getStringMetadata(user, 'name');
}

export function hasGoogleAuthMetadataConflict(
  user: User,
  expectedPortal: GoogleAuthPortal,
  expectedUserType: GoogleAuthUserType,
) {
  const existingPortal = normalizeMetadataValue(getStringMetadata(user, 'portal'));
  const existingUserType = normalizeMetadataValue(getStringMetadata(user, 'user_type'));

  return (
    (existingPortal && existingPortal !== expectedPortal) ||
    (existingUserType && existingUserType !== expectedUserType)
  );
}
